import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupplierInvoiceFileBlob } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Loader2, ExternalLink, FileWarning, RotateCw } from 'lucide-react';

// Which of the three render paths a document takes. Anything that is not a
// PDF or an image gets the honest "no preview" state — never a blank frame.
const KIND_PDF = 'pdf';
const KIND_IMAGE = 'image';
const KIND_OTHER = 'other';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

// MIME type first (the browser sets it on a picked File, and the backend sends
// it as the blob's content-type). The filename extension is only a fallback for
// the case where the type is missing or a generic application/octet-stream.
function documentKind(mimeType, name) {
    const type = (mimeType || '').toLowerCase();
    if (type === 'application/pdf') return KIND_PDF;
    if (type.startsWith('image/')) return KIND_IMAGE;
    const ext = (name || '').toLowerCase().split('.').pop();
    if (ext === 'pdf') return KIND_PDF;
    if (IMAGE_EXTENSIONS.includes(ext)) return KIND_IMAGE;
    return KIND_OTHER;
}

/**
 * Preview pane for a supplier invoice document, shown beside the invoice form
 * so the numbers can be read off the document while they are typed.
 *
 * Two sources, both handled here:
 *   - `file`: a File the admin just picked, BEFORE it is uploaded
 *     (URL.createObjectURL over the local file).
 *   - `filePath`: a file already stored on the invoice, fetched as a blob
 *     through the SAME tenant-isolated endpoint the "View" action uses
 *     (GET /supplier-invoices/file/{path}). A direct URL is deliberately not
 *     constructed — it would carry no Authorization header.
 *
 * `fetchBlob` exists for Phase 25.4 and nothing else: a HELD intake document
 * has no supplier_invoices row, so the default endpoint proves no ownership
 * for its path and 404s. The intake screen passes its own tenant-isolated
 * fetcher rather than this component growing a second copy of the render,
 * kind-detection and object-URL-revocation logic.
 *
 * Every object URL created here is revoked in the effect cleanup, so
 * reopening the dialog repeatedly cannot leak blobs.
 */
export default function SupplierInvoicePreview({
    id,
    file = null,
    filePath = null,
    fileName = null,
    fetchBlob = getSupplierInvoiceFileBlob,
    onOpenExternal = null,
    className,
}) {
    const { t } = useTranslation();
    // status: 'idle' | 'loading' | 'ready' | 'error'
    const [preview, setPreview] = useState({ status: 'idle', url: null, kind: KIND_OTHER });
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        const revoke = () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                objectUrl = null;
            }
        };

        // A newly-picked file wins over the stored one — it is what will
        // replace it on save, so it is what the admin needs to read.
        if (file) {
            objectUrl = URL.createObjectURL(file);
            setPreview({
                status: 'ready',
                url: objectUrl,
                kind: documentKind(file.type, file.name),
            });
            return revoke;
        }

        if (filePath) {
            setPreview({ status: 'loading', url: null, kind: KIND_OTHER });
            fetchBlob(filePath)
                .then((response) => {
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(response.data);
                    setPreview({
                        status: 'ready',
                        url: objectUrl,
                        kind: documentKind(response.data?.type, fileName || filePath),
                    });
                })
                .catch(() => {
                    // A failed fetch must land on a real error state — never
                    // leave the pane spinning forever.
                    if (!cancelled) setPreview({ status: 'error', url: null, kind: KIND_OTHER });
                });
            return () => {
                cancelled = true;
                revoke();
            };
        }

        setPreview({ status: 'idle', url: null, kind: KIND_OTHER });
        return revoke;
        // `fetchBlob` is a dependency like any other, so it must be a STABLE
        // reference — a module-level function, never an inline arrow, which
        // would re-run this effect on every render.
    }, [file, filePath, fileName, fetchBlob, reloadKey]);

    // "Open in a new tab": for a stored file, reuse the parent's authenticated
    // view/download action; for a not-yet-uploaded file, the local object URL
    // is all there is.
    const handleOpen = useCallback(() => {
        if (onOpenExternal) {
            onOpenExternal();
            return;
        }
        if (preview.url) window.open(preview.url, '_blank', 'noopener,noreferrer');
    }, [onOpenExternal, preview.url]);

    const canOpen = !!onOpenExternal || !!preview.url;
    const displayName = file ? file.name : fileName;

    return (
        <div
            id={id}
            className={cn('flex flex-col border border-border bg-muted/20 min-w-0', className)}
            data-testid="supplier-invoice-preview"
        >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/40 shrink-0">
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        {t('supplier_invoices.preview_title')}
                    </p>
                    {displayName && (
                        <p className="text-xs truncate max-w-[220px]" title={displayName}>
                            {displayName}
                            {file && (
                                <span className="text-muted-foreground italic">
                                    {' '}
                                    · {t('supplier_invoices.preview_not_uploaded_yet')}
                                </span>
                            )}
                        </p>
                    )}
                </div>
                {canOpen && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleOpen}
                        className="shrink-0"
                        data-testid="supplier-invoice-preview-open"
                    >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        {t('common.view')}
                    </Button>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-auto bg-background">
                {preview.status === 'loading' && (
                    <div className="h-full flex flex-col items-center justify-center gap-2 p-6 text-muted-foreground">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <p className="text-sm">{t('supplier_invoices.preview_loading')}</p>
                    </div>
                )}

                {preview.status === 'error' && (
                    <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
                        <FileWarning className="w-8 h-8 text-amber-600" />
                        <p className="text-sm text-muted-foreground">
                            {t('supplier_invoices.preview_error')}
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setReloadKey((prev) => prev + 1)}
                            data-testid="supplier-invoice-preview-retry"
                        >
                            <RotateCw className="w-4 h-4 mr-1" />
                            {t('supplier_invoices.preview_retry')}
                        </Button>
                    </div>
                )}

                {preview.status === 'ready' && preview.kind === KIND_PDF && (
                    <object
                        data={preview.url}
                        type="application/pdf"
                        className="w-full h-full min-h-[300px]"
                        aria-label={t('supplier_invoices.preview_alt')}
                    >
                        <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
                            <p className="text-sm text-muted-foreground">
                                {t('supplier_invoices.preview_pdf_fallback')}
                            </p>
                            {canOpen && (
                                <Button type="button" variant="outline" size="sm" onClick={handleOpen}>
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    {t('common.view')}
                                </Button>
                            )}
                        </div>
                    </object>
                )}

                {preview.status === 'ready' && preview.kind === KIND_IMAGE && (
                    <img
                        src={preview.url}
                        alt={t('supplier_invoices.preview_alt')}
                        className="w-full h-auto"
                    />
                )}

                {preview.status === 'ready' && preview.kind === KIND_OTHER && (
                    <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
                        <FileWarning className="w-8 h-8 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                            {t('supplier_invoices.preview_unavailable')}
                        </p>
                        {canOpen && (
                            <Button type="button" variant="outline" size="sm" onClick={handleOpen}>
                                <ExternalLink className="w-4 h-4 mr-1" />
                                {t('common.view')}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
